import { TableColumn } from '../../../../table/interfaces/table';
import { JobObject, JobObjectStatus } from '../../../interfaces/job';
import { JobObjectListGlobalContext } from './interface';
import { Icon } from '../../../../icon/Icon';
import { Tooltip } from '../../../../tooltip/Tooltip';
import { Calendar } from '../../../../shared/components/Calendar/Calendar';
import Duration from '../../../../shared/components/Duration/Duration';
import { RenderEventCell } from '../../EventCell';
import { GenericStatusLabel } from '../../../../shared/components/GenericStatusLabel/GenericStatusLabel';
import { LatencyWrapper } from '../../LatencyWrapper/LatencyWrapper';
import { shouldRenderObjectDetails } from '../../../utils';
import HeartBeatIndicator from '../../../../shared/components/HeartBeatIndicator/HeartBeatIndicator';

export const getObjectTableColumns = (
  isLoadOnly: boolean = false,
  latencyNotApplicable: boolean = false
): TableColumn<JobObject, JobObjectListGlobalContext>[] => [
  {
    id: 'name',
    field: 'objectName',
    headerName: 'Object Name',
    width: 130,
    isFlexible: true,
    wrapOverflowingCellText: true,
    renderCell: params => {
      const isCollapseIconRendered = shouldRenderObjectDetails(
        isLoadOnly,
        params.row.data.objectStages
      );

      return (
        <div className='center-flex-row overflow-hidden'>
          {isCollapseIconRendered ? (
            <Icon
              className='mr-4'
              dataId={params.row.data.objectName}
              name={
                params.globalContext?.rowsExpandedState[params.row.data.objectName]
                  ? 'dropdown-arrow'
                  : 'right-arrow'
              }
            />
          ) : null}

          <Tooltip dataId='' title={params.row.data.objectName} enableOnTextOverflow>
            <span className='text-ellipsis'>{params.row.data.objectName}</span>
          </Tooltip>
        </div>
      );
    },
    canSort: true,
    sortKey: 'object_name'
  },
  {
    id: 'started_at',
    field: 'startedAt',
    headerName: 'Started At',
    width: 140,
    renderCell: params => (
      <Calendar value={params.row.data.startedAt} options={{ hideZeroTimestamp: true }} />
    )
  },
  {
    id: 'duration',
    field: 'duration',
    headerName: 'Duration',
    width: 80,
    renderCell: params => (
      <Duration
        duration={params.row.data.duration}
        options={{ useAbbr: true, separateUnits: true }}
      />
    )
  },
  {
    id: 'latency',
    field: 'latency',
    headerName: 'Max Latency',
    width: 90,
    renderCell: params => (
      <LatencyWrapper
        latency={params.row.data.latency}
        latencyNotApplicable={latencyNotApplicable}
      />
    )
  },
  {
    id: 'offset',
    field: 'offset',
    headerName: 'Offset',
    width: 80,
    renderCell: params => (
      <Tooltip dataId='' title={params.row.data.offset} enableOnTextOverflow>
        <span className='text-ellipsis'>{params.row.data.offset}</span>
      </Tooltip>
    )
  },
  {
    id: 'ingested',
    field: 'ingested',
    headerName: '# Ingested',
    description: 'Count of Ingested Events',
    width: 80,
    justifyEnd: true,
    renderCell: params => <RenderEventCell count={params.row.data.eventsIngested} />,
    canSort: true,
    sortKey: 'so_ingested'
  },
  {
    id: 'loaded',
    field: 'loaded',
    headerName: '# Loaded',
    description: 'Count of Loaded Events',
    width: 70,
    justifyEnd: true,
    renderCell: params => <RenderEventCell count={params.row.data.eventsLoaded} />,
    canSort: true,
    sortKey: 'so_loaded'
  },
  {
    id: 'failed',
    field: 'failed',
    headerName: '# Failed',
    description: 'Count of Failed Events',
    width: 80,
    justifyEnd: true,
    renderCell: params => <RenderEventCell count={params.row.data.eventsFailed} />,
    canSort: true,
    sortKey: 'so_failed'
  },
  {
    id: 'status',
    field: 'status',
    headerName: 'Status',
    width: 178,
    justifyEnd: true,
    renderCell: params => {
      return (
        <GenericStatusLabel
          withPrefixComponent={params.row.data.status.value === JobObjectStatus.IN_PROGRESS}
          PrefixComponent={HeartBeatIndicator as React.ComponentType}
          prefixComponentProps={{
            status: params.row.data.status,
            updatedAt: params.row.data.objectStages.filter(stage => stage.stage === 'ingest')[0]
              ?.updatedAt
          }}
          status={params.row.data.status}
        />
      );
    }
  }
];
